import { useId } from 'react'

export interface FieldIds {
  controlId: string
  hintId: string
  errorId: string
  /** Value for aria-describedby, or undefined when there is nothing to describe. */
  describedBy: string | undefined
}

interface UseFieldIdsOptions {
  id?: string
  hasHint?: boolean
  hasError?: boolean
}

/**
 * Ids for a labelled control.
 *
 * `useId` is called unconditionally on purpose. The previous inline pattern
 * (`id ?? \`input-${useId()}\``) short-circuits, so the hook only ran when the
 * caller omitted `id` — a Rules of Hooks violation that changes the hook count
 * whenever a component's `id` prop goes from defined to undefined.
 */
export function useFieldIds({ id, hasHint = false, hasError = false }: UseFieldIdsOptions = {}): FieldIds {
  const generatedId = useId()
  const controlId = id ?? `field-${generatedId}`
  const hintId = `${controlId}-hint`
  const errorId = `${controlId}-error`

  const describedBy = [hasHint ? hintId : null, hasError ? errorId : null].filter(Boolean).join(' ') || undefined

  return { controlId, hintId, errorId, describedBy }
}
